import { FunctionComponent } from "react";
import { GenericError } from "src/components/Feedback/GenericError";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import { replaceCIDsWithInlineImages } from "src/components/WysiwygEditor/lexical/utils/converters";
import {
  MESSAGE_TEMPLATE_CATEGORIES,
  MessageTemplateCategory,
} from "src/constants";
import { MessageContextSchema } from "src/generated/hooks/avela-ai/requests";
import { useGetEnglishLanguageName } from "src/hooks/useGetEnglishLanguageName";
import { useOrganization } from "src/hooks/useOrganization";
import { useTranslationOptions } from "src/hooks/useTranslationOptions";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import {
  defaultTemplateRankDisabled,
  defaultTemplateRankEnabled,
} from "../../messageTemplateBody";
import {
  EditMessageTemplateForm,
  EditMessageTemplateSkeleton,
} from "./EditMessageTemplateForm";
import {
  RichContentDocumentWithContentId,
  useMessageTemplateRemoteData,
} from "./hooks/useMessageTemplateRemoteData";
import { AutomaticMessageFormType, MessageTemplateContent } from "./schemas";

function buildInitialValues(
  templateType: GQL.message_template_type_enum,
  template: GQL.GetMessageTemplate["message_template_standard"][0] | undefined,
  defaultTemplate: typeof defaultTemplateRankEnabled,
  richContents: RichContentDocumentWithContentId[],
  getLanguageName: (code: string) => string
): AutomaticMessageFormType {
  if (!template) {
    const defaultT = defaultTemplate[templateType];
    const messages: MessageTemplateContent[] = [
      {
        subject: defaultT.emailSubject,
        html: defaultT.emailHtml,
        text: defaultT.emailText,
        lexical: undefined,
        smsText: defaultT.sms,
        smsLexical: undefined,
        language: "en",
        language_name: getLanguageName("en"),
      },
    ];
    return { enabled: false, messages, removedPersistedRichContentIds: [] };
  }

  // HTML  content is saved with CIDs, so we need to replace them with inline images
  // when we build the initial values, since "Preview" will use HTML to render the content
  // and it does not support CIDs.
  if (richContents.length > 0) {
    const messages: MessageTemplateContent[] = richContents.map((rc) => {
      const language = rc.language ?? "en";
      const languageName = getLanguageName(language);
      const html = rc.html
        ? replaceCIDsWithInlineImages(rc.html, rc.attachments ?? [])
        : "";
      const subject = rc.subject ?? template.email_subject;
      return {
        ...rc,
        subject,
        html,
        language,
        language_name: languageName,
      };
    });
    return {
      enabled: template.enabled,
      messages,
      removedPersistedRichContentIds: [],
    };
  }

  // return a single message with the template data from the database
  const messages = [
    {
      subject: template.email_subject,
      lexical: undefined,
      html: template.email_markup,
      text: template.email_plain_text,
      smsText: template.sms_body,
      smsLexical: undefined,
      language: "en",
      language_name: getLanguageName("en"),
    } satisfies MessageTemplateContent,
  ];
  return {
    enabled: template.enabled,
    messages,
    removedPersistedRichContentIds: [],
  };
}

export const EditMessageTemplate: FunctionComponent = () => {
  const { isViewOnly = "false" } = Url.OrgAdmin.MessageTemplate.useParams();
  const { remoteData } = useMessageTemplateRemoteData();
  const translationOptionsRD = useTranslationOptions().castError<Error>();
  const getLanguageName = useGetEnglishLanguageName();
  const organization = useOrganization().castError<Error>();

  return (
    <GQLRemoteDataView
      remoteData={RD.toTuple3(remoteData, translationOptionsRD, organization)}
      error={() => <GenericError />}
      loading={<EditMessageTemplateSkeleton />}
    >
      {([
        {
          formTemplateId,
          enrollmentPeriodId,
          templateType,
          template: data,
          richContents,
          schoolSpecificEnabled,
          schoolId,
        },
        translationOptions,
        organization,
      ]) => {
        const template = schoolSpecificEnabled
          ? data.message_template_school_specific[0]
          : data.message_template_standard[0];

        const rankingEnabled =
          data.form_template_by_pk?.sections[0]?.schools_ranking_section
            ?.ranking_enabled ?? false;

        const defaultTemplate = rankingEnabled
          ? defaultTemplateRankEnabled
          : defaultTemplateRankDisabled;

        const isOfferEnabled =
          data.form_template_by_pk?.lottery_offers_enabled ?? false;
        const isOfferRelated = MESSAGE_TEMPLATE_CATEGORIES.find(
          (category) =>
            category.key === MessageTemplateCategory.LotteryAndOffers
        )?.messageTemplateTypes.includes(templateType);

        const messageContext: MessageContextSchema = {
          trigger_event_name: templateType,
          form_template: {
            id: formTemplateId,
            name: data.form_template_by_pk?.name ?? "",
          },
          enrollment_period: {
            id: enrollmentPeriodId,
            name: data.form_template_by_pk?.enrollment_period?.name ?? "",
          },
          organization: {
            id: organization.id,
            name: organization.name,
          },
          school: schoolId
            ? {
                id: schoolId,
                name: data.school_by_pk?.name ?? "",
              }
            : null,
        };

        const initialValues = buildInitialValues(
          templateType,
          template,
          defaultTemplate,
          richContents,
          getLanguageName
        );

        return (
          <EditMessageTemplateForm
            organization={organization}
            formTemplateId={formTemplateId}
            enrollmentPeriodId={enrollmentPeriodId}
            messageTemplateType={templateType}
            initialValues={initialValues}
            existingTemplateId={template?.id ?? null}
            schoolId={schoolId}
            schoolName={data.school_by_pk?.name ?? null}
            schoolSpecificEnabled={schoolSpecificEnabled}
            isDisabled={!isOfferEnabled && isOfferRelated}
            isViewOnly={isViewOnly === "true"}
            configuredLanguages={
              translationOptions.disabled ? [] : translationOptions.languages
            }
            messageContext={messageContext}
          />
        );
      }}
    </GQLRemoteDataView>
  );
};
